/* KUTEÈ scroll-snap carousel: one-card arrows + optional infinite loop.
   Wires [data-ku-carousel] blocks: a .ku-scroller track + optional
   [data-ku-prev]/[data-ku-next] buttons.

   Loop (data-ku-loop="true"): the product set is cloned `sides` times
   before and after the real one, so the track reads
   [clones…][real set][clones…]. The position is wrapped back onto the
   real set by whole set-widths — the content either side is identical,
   so the wrap is invisible.

   Wrapping happens on three occasions, and all three matter:
   - DURING scrolling, as soon as the position leaves the safe band.
     Waiting for the scroll to stop let a hard fling reach the true end
     of the track: empty space past the last card (rubber-band on a Mac)
     and then a visible jump when the wrap finally ran.
   - when scrolling settles, to return to the canonical position.
   - before each arrow press, so repeated presses can't walk out of the
     band faster than the scroll handler corrects.
   Programmatic (arrow) scrolls hold the during-scroll guard off, since
   touching scrollLeft mid-animation would cancel the glide.

   Images are warmed after wiring: lazy loading only looks ahead in the
   VIEWPORT, so horizontally off-screen cards stayed unloaded and drew
   as white-on-white — indistinguishable from blank space. */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setupLoop = (track) => {
    const originals = Array.from(track.children);
    const count = originals.length;
    if (count === 0) return null;

    // A set that already fits in view has nothing to wrap around.
    if (track.scrollWidth - track.clientWidth <= 4) return null;

    // Enough runway either side that a hard fling can't cross it.
    const baseWidth = Math.max(track.scrollWidth, 1);
    const sides = Math.min(3, Math.max(1, Math.ceil((2 * track.clientWidth) / baseWidth)));

    const cloneSet = () => {
      const fragment = document.createDocumentFragment();
      originals.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.dataset.kuClone = 'true';
        /* ku-reveal.js hides below-the-fold cards (opacity 0) and reveals
           them via an IntersectionObserver that only watches the cards
           present when it primed. A clone inherits the hidden class and is
           never observed, so it stays invisible forever — that was the
           "blank" after the last bag, and the wrap out of that invisible
           stretch was the "jump". Clones are duplicates, so they get the
           revealed state outright; --in also survives a later prime(). */
        clone.classList.remove('ku-reveal');
        clone.classList.add('ku-reveal--in');
        clone.style.removeProperty('--ku-reveal-delay');
        // Clones are decoration: out of the a11y tree and the tab order
        // (quick-add still works — its listener is delegated on document).
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        fragment.appendChild(clone);
      });
      return fragment;
    };

    for (let i = 0; i < sides; i += 1) track.insertBefore(cloneSet(), track.firstChild);
    for (let i = 0; i < sides; i += 1) track.appendChild(cloneSet());

    /* Measured once per layout rather than per scroll event: reading
       offsetLeft while scrolling would force a layout every frame. */
    let setWidth = 0;
    let home = 0;
    const measure = () => {
      const first = track.children[0];
      const firstOriginal = track.children[count * sides];
      if (!first || !firstOriginal) return;
      home = firstOriginal.offsetLeft - first.offsetLeft;
      setWidth = home / sides;
    };

    const normalize = () => {
      if (setWidth <= 0) return;
      const pos = track.scrollLeft;
      const wrapped = ((((pos - home) % setWidth) + setWidth) % setWidth) + home;
      if (Math.abs(wrapped - pos) > 1) track.scrollLeft = wrapped;
    };

    // Correct before the scroll can reach either end of the track.
    const guard = () => {
      if (setWidth <= 0) return;
      const pos = track.scrollLeft;
      if (pos < home - setWidth * 0.25 || pos > home + setWidth * 0.75) normalize();
    };

    measure();
    track.scrollLeft = home;

    let held = false;
    let holdTimer = null;
    const hold = (ms = 700) => {
      held = true;
      clearTimeout(holdTimer);
      holdTimer = setTimeout(() => {
        held = false;
      }, ms);
    };

    let settle = null;
    track.addEventListener(
      'scroll',
      () => {
        if (!held) guard();
        clearTimeout(settle);
        settle = setTimeout(normalize, 120);
      },
      { passive: true }
    );

    let resized = null;
    window.addEventListener('resize', () => {
      clearTimeout(resized);
      resized = setTimeout(() => {
        measure();
        normalize();
      }, 200);
    });

    return { normalize, hold };
  };

  const wire = (root) => {
    const track = root.querySelector('.ku-scroller');
    if (!track || root.dataset.kuWired === 'true') return;
    root.dataset.kuWired = 'true';

    /* Per-card reveal is wrong for a horizontal track: cards parked off to
       the right never intersect the viewport, so they'd sit invisible until
       scrolled to (and clones never at all). Reveal the whole track as one
       group the first time the carousel comes into view. */
    const revealTrack = () => {
      track.querySelectorAll('.ku-card').forEach((card) => {
        card.classList.remove('ku-reveal');
        card.classList.add('ku-reveal--in');
      });
    };
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealTrack();
            obs.disconnect();
          });
        },
        { threshold: 0.05 }
      );
      seen.observe(root);
    } else {
      revealTrack();
    }

    const loop = root.dataset.kuLoop === 'true' ? setupLoop(track) : null;

    /* Warm the track off the critical path (LCP untouched), bounded so a
       50-product carousel can't flood a slow connection. */
    setTimeout(() => {
      Array.from(track.querySelectorAll('img'))
        .slice(0, 48)
        .forEach((img) => {
          if (img.loading === 'lazy') img.loading = 'eager';
        });
    }, 1500);

    // One card per press — the carousel advances bag by bag, either way.
    const step = () => {
      const card = track.firstElementChild;
      if (!card) return Math.max(track.clientWidth * 0.8, 240);
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const width = card.getBoundingClientRect().width + gap;
      return width > 0 ? width : Math.max(track.clientWidth * 0.8, 240);
    };

    const go = (direction) => {
      if (loop) {
        loop.normalize();
        loop.hold(); // don't let the guard cancel our own glide
      }
      track.scrollBy({
        left: direction * step(),
        behavior: reduceMotion() ? 'auto' : 'smooth',
      });
    };

    const prev = root.querySelector('[data-ku-prev]');
    const next = root.querySelector('[data-ku-next]');
    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));

    /* Arrows only when there is something to scroll — a carousel holding
       exactly as many products as it shows has inert arrows, which read
       as broken. Re-checked on resize and when products are added. */
    const syncArrows = () => {
      const overflows = track.scrollWidth - track.clientWidth > 4;
      root.classList.toggle('ku-featured--no-scroll', !overflows);
    };
    syncArrows();
    let resync = null;
    window.addEventListener('resize', () => {
      clearTimeout(resync);
      resync = setTimeout(syncArrows, 200);
    });
  };

  const init = () => document.querySelectorAll('[data-ku-carousel]').forEach(wire);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
